import React from "react"
import {
  Box,
  Divider, Flex, Stack, Text,
} from "@chakra-ui/react"
import ConfirmDelete from "components/dialogs/Confirm/ConfirmDelete"
import Menu, { MenuItem } from "components/dialogs/Menu"
import useModal from "services/useModal"
import { DioboxEvent, ModalName, TextMessage } from "sharedTypes"
import Card from "components/layout/Card"
import { asDate } from "utilities/strings"
import useWhatsAppTemplate from "services/useWhatsAppTemplate"
import { getTemplateMediaUrl } from "utilities/whatsappTemplates"
import TextBubble from "modals/TextMessage/PreviewTextMessage/Phone/TextBubble"
import MessageBubbleContent from "components/MessageBubbleContent"
import whatsappBg from "assets/images/phone/whatsapp-bg.webp"
import FormIntegrations from "components/FormIntegrations/FormIntegrations"
import useMergeTags from "services/useMergeTags"
import { useForms } from "queries/forms"
import { Route, useHistory, useRouteMatch } from "react-router-dom"
import { eventTextMessageReportPath } from "utilities/routes"
import { putEvent } from "api/events"
import { useQueryClient } from "react-query"
import { setCurrentEvent } from "context/actions"
import { useCurrentUser } from "queries/users"
import { handleError } from "utilities/toasts"
import OptInInfoBox from "../OptInInfoBox"
import TextMessageEngagements from "./TextMessageEngagements"
import TextReport from "../TextReport"

type Props = {
  selectedTextMessage: TextMessage
  onSave: (textMessage: TextMessage) => void
  onDelete: () => void
  event: DioboxEvent
  onDuplicate: (textMessage: TextMessage) => void
}

const SentTextMessage = ({
  selectedTextMessage: {
    name,
    sentAt,
    body,
    externalId,
    deliveryCounts,
    channel,
    whatsappContentSid,
    whatsappContentVariables,
  },
  selectedTextMessage,
  onSave,
  onDelete,
  event: { id: eventId },
  event,
  onDuplicate,
}: Props) => {
  const showModal = useModal()
  const { getFormsFromTextBody } = useMergeTags({ eventId })
  const { template } = useWhatsAppTemplate(whatsappContentSid)
  const { data: formsData } = useForms(eventId)
  const history = useHistory()
  const { path } = useRouteMatch()
  const queryClient = useQueryClient()
  const currentUser = useCurrentUser()

  const formsList = formsData?.data ?? []

  const isOptInText = template?.messageType === "opt_in"
  const isDefaultOptInText = isOptInText
    && selectedTextMessage.id !== undefined
    && event.defaultOptInTextId === selectedTextMessage.id

  // Extract form IDs for Form Integration section
  const rawFormText = channel === "whatsapp" && whatsappContentVariables
    ? Object.values(whatsappContentVariables).join(" ")
    : body ?? ""
  const forms = getFormsFromTextBody(rawFormText)

  const mediaUrl = template ? getTemplateMediaUrl(template, whatsappContentVariables) : undefined

  const templateButtons = template?.buttons

  if (!sentAt) {
    return null
  }

  return (
    <>
      <Stack spacing={7}>
        <Flex justifyContent="space-between">
          <Text fontSize="2xl" fontWeight="semibold">
            {name}
          </Text>
          <Menu
            noPadding
            buttonText="Text Options"
            menuItems={[
              <MenuItem
                key="rename"
                onClick={() => showModal(ModalName.EditTextMessageName,
                  { onSubmit: onSave, initialValues: selectedTextMessage })}
              >Rename
              </MenuItem>,
              <MenuItem key="duplicate" onClick={() => onDuplicate(selectedTextMessage)}>Duplicate Text</MenuItem>,
              <Divider key="divider-1" />,
              <MenuItem
                key="report"
                onClick={() => history.push(
                  eventTextMessageReportPath(eventId, externalId),
                )}
              >View Text Report
              </MenuItem>,
              <MenuItem
                key="export"
                onClick={() => showModal(ModalName.ExportTextReport,
                  { id: externalId })}
              >
                Export Text Report
              </MenuItem>,
              <Divider key="divider-2" />,
              <ConfirmDelete key="delete" type="Text">
                <MenuItem onClick={onDelete} color="red.700">Delete</MenuItem>
              </ConfirmDelete>,
            ]}
          />
        </Flex>
        {isOptInText && (
          <OptInInfoBox
            isDefault={isDefaultOptInText}
            onSetAsDefault={async () => {
              if (selectedTextMessage.id === undefined) return
              try {
                const { data } = await putEvent(event.id, {
                  ...event,
                  defaultOptInTextId: selectedTextMessage.id,
                })
                setCurrentEvent(queryClient, data, currentUser)
              } catch (error) {
                handleError(error)
              }
            }}
          />
        )}
        <Card>
          <Flex flexDirection="column" alignItems="center">
            <Text fontWeight="bold" mb={2}>Sent: {asDate(sentAt)}</Text>
            <Flex
              w="lg"
              borderRadius="md"
              overflow="hidden"
              justifyContent="center"
              {...channel === "whatsapp" ? {
                bgImage: `url(${whatsappBg})`,
                bgSize: "cover",
                bgPos: "center",
                borderWidth: 1,
                borderColor: "gray.200",
              } : {}}
              p={5}
            >
              <TextBubble
                text={<MessageBubbleContent body={body} forms={formsList} />}
                mediaUrl={mediaUrl}
                buttons={templateButtons}
                channel={channel}
              />
            </Flex>
            <Stack w="full" mt={12} spacing={12}>
              <TextMessageEngagements
                deliveryCounts={deliveryCounts}
                fallbackCounts={selectedTextMessage.fallbackCounts}
                channel={channel}
              />
              {forms.length > 0 && (
              <Box>
                <Box borderBottomWidth={1} pb={4} mb={3}><Text fontSize="2xl">Form Integration</Text></Box>
                <Box ml={5}>
                  <FormIntegrations
                    event={event}
                    forms={forms}
                    deletedFormsCount={0}
                    entity="text"
                  />
                </Box>
              </Box>
              )}
            </Stack>
          </Flex>
        </Card>
      </Stack>
      <Route path={`${path}/report`}>
        <TextReport textMessage={selectedTextMessage} />
      </Route>
    </>
  )
}

export default SentTextMessage
